/**
 * Schwab-style login panel for Nigastock Pro - Active Trader.
 * Wires to useAuth() for the real Cognito flow; the offline demo mounts
 * a mock provider that accepts demo/password + any 6-digit MFA code.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';

export type StartPage = 'Welcome' | 'Dashboard' | 'Accounts Summary' | 'Charts' | 'Trade' | 'Scans' | 'Tools';

const START_PAGES: StartPage[] = ['Welcome', 'Dashboard', 'Accounts Summary', 'Charts', 'Trade', 'Scans', 'Tools'];
const LANGS = ['English', 'Francais', 'Hausa', 'Yoruba', 'Igbo'];

const REMEMBER_KEY = 'nigastock.loginId';
const STARTPAGE_KEY = 'nigastock.startPage';

function safeGet(k: string): string | null {
  try { return typeof localStorage !== 'undefined' ? localStorage.getItem(k) : null; } catch { return null; }
}
function safeSet(k: string, v: string) {
  try { localStorage.setItem(k, v); } catch {}
}
function safeDel(k: string) {
  try { localStorage.removeItem(k); } catch {}
}

interface Props { demoHint?: string }

export function LoginPanel({ demoHint }: Props = {}) {
  const auth = useAuth();
  const [loginId, setLoginId] = useState(() => safeGet(REMEMBER_KEY) ?? '');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(() => !!safeGet(REMEMBER_KEY));
  const [startPage, setStartPage] = useState<StartPage>(
    () => (safeGet(STARTPAGE_KEY) as StartPage) ?? 'Dashboard',
  );
  const [lang, setLang] = useState('English');
  const [showPw, setShowPw] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | undefined>();

  // Signup mode state
  const [mode, setMode] = useState<'login' | 'signup' | 'confirm' | 'forgot-request' | 'forgot-confirm'>('login');
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPw, setSignupPw] = useState('');
  const [signupPw2, setSignupPw2] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [infoMsg, setInfoMsg] = useState<string | undefined>();

  // Forgot-password mode state
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPw, setForgotNewPw] = useState('');
  const [forgotNewPw2, setForgotNewPw2] = useState('');

  useEffect(() => {
    if (remember && loginId) safeSet(REMEMBER_KEY, loginId);
    else if (!remember) safeDel(REMEMBER_KEY);
  }, [remember, loginId]);
  useEffect(() => { safeSet(STARTPAGE_KEY, startPage); }, [startPage]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalErr(undefined);
    if (!loginId || !password) { setLocalErr('Enter your Login ID and Password.'); return; }
    setBusy(true);
    try { await auth.signIn(loginId.trim(), password); }
    catch (err: any) { setLocalErr(err?.message ?? 'Sign-in failed. Check your credentials.'); }
    finally { setBusy(false); }
  }

  async function onMfa(e: React.FormEvent) {
    e.preventDefault();
    setLocalErr(undefined);
    if (code.length < 4) { setLocalErr('Enter the 6-digit code from your authenticator.'); return; }
    setBusy(true);
    try { await auth.completeMfa(code); }
    catch (err: any) { setLocalErr(err?.message ?? 'Invalid code. Try again.'); }
    finally { setBusy(false); }
  }

  const err = localErr ?? auth.error;
  const inMfa = auth.status === 'mfa_required';

  // When signUp completes successfully, AuthContext sets status to 'sign_up_pending'.
  // Auto-switch to confirm mode so the user sees the verification-code form.
  useEffect(() => {
    if (auth.status === 'sign_up_pending' && mode !== 'confirm') {
      setMode('confirm');
      setInfoMsg('Check your email for a 6-digit verification code.');
    }
  }, [auth.status, mode]);

  async function onSignUpSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalErr(undefined);
    setInfoMsg(undefined);
    if (signupPw !== signupPw2) { setLocalErr('Passwords do not match.'); return; }
    if (signupPw.length < 8) { setLocalErr('Password must be at least 8 characters.'); return; }
    if (!signupName.trim()) { setLocalErr('Please enter your full name.'); return; }
    if (!signupEmail.includes('@')) { setLocalErr('Please enter a valid email address.'); return; }
    setBusy(true);
    try {
      const fullName = signupName.trim();
      const firstName = fullName.split(' ')[0] || fullName;
      await auth.signUp(signupEmail.trim(), signupPw, fullName, firstName);
    }
    catch (e: any) { setLocalErr(e?.message ?? 'sign-up failed'); }
    finally { setBusy(false); }
  }

  async function onConfirmSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalErr(undefined);
    setInfoMsg(undefined);
    if (!/^\d{6}$/.test(confirmCode.trim())) { setLocalErr('Code must be 6 digits.'); return; }
    setBusy(true);
    try {
      await auth.confirmSignUp(signupEmail.trim(), confirmCode.trim());
      // Success: switch back to login mode with the email pre-filled
      setMode('login');
      setLoginId(signupEmail.trim());
      setInfoMsg('Email verified! Sign in below to continue.');
      setSignupPw(''); setSignupPw2(''); setConfirmCode('');
    } catch (e: any) {
      setLocalErr(e?.message ?? 'confirmation failed');
    } finally { setBusy(false); }
  }

  async function onResendCode() {
    setLocalErr(undefined);
    setInfoMsg(undefined);
    setBusy(true);
    try {
      await auth.resendConfirmCode(signupEmail.trim());
      setInfoMsg('A new code has been sent. Check your email.');
    } catch (e: any) { setLocalErr(e?.message ?? 'resend failed'); }
    finally { setBusy(false); }
  }

  async function onForgotSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalErr(undefined);
    setInfoMsg(undefined);
    if (!forgotEmail.includes('@')) { setLocalErr('Please enter a valid email address.'); return; }
    setBusy(true);
    try {
      await auth.forgotPassword(forgotEmail.trim());
      // status flips to 'forgot_pending' inside AuthContext; switch UI to next step
      setMode('forgot-confirm');
      setInfoMsg('Check your email for a 6-digit verification code.');
    } catch (e: any) { setLocalErr(e?.message ?? 'password reset failed'); }
    finally { setBusy(false); }
  }

  async function onConfirmForgotSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalErr(undefined);
    setInfoMsg(undefined);
    if (!/^\d{6}$/.test(forgotCode.trim())) { setLocalErr('Code must be 6 digits.'); return; }
    if (forgotNewPw !== forgotNewPw2) { setLocalErr('Passwords do not match.'); return; }
    if (forgotNewPw.length < 8) { setLocalErr('Password must be at least 8 characters.'); return; }
    setBusy(true);
    try {
      await auth.confirmForgotPassword(forgotEmail.trim(), forgotCode.trim(), forgotNewPw);
      // Success — switch to login mode with the email pre-filled
      setMode('login');
      setLoginId(forgotEmail.trim());
      setInfoMsg('Password reset! Sign in with your new password.');
      setForgotCode(''); setForgotNewPw(''); setForgotNewPw2('');
    } catch (e: any) {
      setLocalErr(e?.message ?? 'confirm password failed');
    } finally { setBusy(false); }
  }

  return (
    <div className="nsl-page">
      <div className="nsl-card" role="region" aria-label="Sign in">
        <h1 className="nsl-title">{
          mode === 'signup' ? 'Create your NigaStock account' :
          mode === 'confirm' ? 'Verify your email' :
          mode === 'forgot-request' ? 'Reset your password' :
          mode === 'forgot-confirm' ? 'Set a new password' :
          'Log in to NigaStock'
        }</h1>

        {infoMsg && <div className="nsl-info" style={{ background: '#e6f4ea', border: '1px solid #34a853', color: '#1e6f3a', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{infoMsg}</div>}

        {mode === 'login' && !inMfa && (
          <form onSubmit={onSubmit} className="nsl-form" noValidate>
            <label className="nsl-label" htmlFor="nsl-loginid">Login ID</label>
            <input
              id="nsl-loginid"
              className="nsl-input"
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              autoComplete="username"
              autoFocus
              aria-required="true"
              aria-invalid={!!err}
            />

            <label className="nsl-label" htmlFor="nsl-password">Password</label>
            <div className="nsl-pw-wrap">
              <input
                id="nsl-password"
                className="nsl-input nsl-pw"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                aria-required="true"
                aria-invalid={!!err}
              />
              <button
                type="button"
                className="nsl-pw-eye"
                aria-label={showPw ? 'Hide password' : 'Show password'}
                aria-pressed={showPw}
                onClick={() => setShowPw((v) => !v)}
              >{showPw ? 'Hide' : 'Show'}</button>
            </div>

            <label className="nsl-check">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span>Remember Login ID</span>
            </label>

            <label className="nsl-label" htmlFor="nsl-startpage">Start Page</label>
            <select
              id="nsl-startpage"
              className="nsl-select"
              value={startPage}
              onChange={(e) => setStartPage(e.target.value as StartPage)}
            >
              {START_PAGES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>

            <button
              type="submit"
              className="nsl-submit"
              disabled={busy || !loginId || !password}
            >{busy ? 'Signing in...' : 'Log In'}</button>

            <div className="nsl-help">
              Forgot{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); alert('Contact support@nigastock.ng to recover your Login ID.'); }}>Login ID</a>
              {' '}or{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); setMode('forgot-request'); setForgotEmail(loginId); setLocalErr(undefined); setInfoMsg(undefined); }}>Password?</a>
            </div>

            <div className="nsl-foot">
              <a className="nsl-new" href="#" onClick={(e) => { e.preventDefault(); setMode('signup'); setLocalErr(undefined); setInfoMsg(undefined); }}>New<br/>User?</a>
              <span className="nsl-sep" aria-hidden="true" />
              <select
                className="nsl-lang"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                aria-label="Language"
              >
                {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>

            {err && <div className="nsl-err" role="alert">{err}</div>}

            <div className="nsl-regs">SEC-regulated &middot; NDPR-compliant &middot; 256-bit TLS</div>
            <div className="nsl-legal">
              <a href="legal/terms.html" target="_blank" rel="noopener">Terms of Service</a>
              <a href="legal/privacy.html" target="_blank" rel="noopener">Privacy Policy</a>
              <a href="legal/risk.html" target="_blank" rel="noopener">Risk Disclosure</a>
              <a href="legal/fx-risk.html" target="_blank" rel="noopener">FX Risk</a>
              <a href="legal/ndpr.html" target="_blank" rel="noopener">NDPR Compliance</a>
            </div>
            {demoHint && <div className="nsl-demo-hint">{demoHint}</div>}
          </form>
        )}

        {inMfa && (
          <form onSubmit={onMfa} className="nsl-form" noValidate>
            <div className="nsl-mfa-intro">
              We sent a one-time code to your authenticator app.
              Trading actions require MFA per SEC safeguards.
            </div>
            <label className="nsl-label" htmlFor="nsl-code">Verification Code</label>
            <input
              id="nsl-code"
              className="nsl-input nsl-mono"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 8))}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={8}
              aria-required="true"
            />
            <button
              type="submit"
              className="nsl-submit"
              disabled={busy || code.length < 4}
            >{busy ? 'Verifying...' : 'Verify & Launch'}</button>
            {err && <div className="nsl-err" role="alert">{err}</div>}
            <div className="nsl-regs">SEC-regulated &middot; NDPR-compliant &middot; 256-bit TLS</div>
            <div className="nsl-legal">
              <a href="legal/terms.html" target="_blank" rel="noopener">Terms of Service</a>
              <a href="legal/privacy.html" target="_blank" rel="noopener">Privacy Policy</a>
              <a href="legal/risk.html" target="_blank" rel="noopener">Risk Disclosure</a>
              <a href="legal/fx-risk.html" target="_blank" rel="noopener">FX Risk</a>
              <a href="legal/ndpr.html" target="_blank" rel="noopener">NDPR Compliance</a>
            </div>
          </form>
        )}

        {mode === 'signup' && (
          <form onSubmit={onSignUpSubmit} className="nsl-form" noValidate>
            <label className="nsl-label" htmlFor="nsl-signup-name">Full Name</label>
            <input
              id="nsl-signup-name"
              className="nsl-input"
              value={signupName}
              onChange={(e) => setSignupName(e.target.value)}
              autoComplete="name"
              autoFocus
              required
            />

            <label className="nsl-label" htmlFor="nsl-signup-email">Email Address</label>
            <input
              id="nsl-signup-email"
              type="email"
              className="nsl-input"
              value={signupEmail}
              onChange={(e) => setSignupEmail(e.target.value)}
              autoComplete="email"
              required
            />

            <label className="nsl-label" htmlFor="nsl-signup-pw">Password</label>
            <input
              id="nsl-signup-pw"
              type="password"
              className="nsl-input"
              value={signupPw}
              onChange={(e) => setSignupPw(e.target.value)}
              autoComplete="new-password"
              required
            />
            <div className="nsl-hint" style={{ fontSize: 11, color: '#6b7280', marginTop: -8, marginBottom: 8 }}>
              At least 8 characters with uppercase, lowercase, number, and symbol.
            </div>

            <label className="nsl-label" htmlFor="nsl-signup-pw2">Confirm Password</label>
            <input
              id="nsl-signup-pw2"
              type="password"
              className="nsl-input"
              value={signupPw2}
              onChange={(e) => setSignupPw2(e.target.value)}
              autoComplete="new-password"
              required
            />

            {err && <div className="nsl-err" role="alert">{err}</div>}

            <button type="submit" className="nsl-btn" disabled={busy}>
              {busy ? 'Creating account...' : 'Create Account'}
            </button>

            <div className="nsl-help" style={{ marginTop: 12 }}>
              Already have an account?{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); setMode('login'); setLocalErr(undefined); setInfoMsg(undefined); }}>Sign in</a>
            </div>

            <div className="nsl-regs">SEC-regulated &middot; NDPR-compliant &middot; 256-bit TLS</div>
            <div className="nsl-legal">
              <a href="legal/terms.html" target="_blank" rel="noopener">Terms of Service</a>
              <a href="legal/privacy.html" target="_blank" rel="noopener">Privacy Policy</a>
              <a href="legal/risk.html" target="_blank" rel="noopener">Risk Disclosure</a>
              <a href="legal/fx-risk.html" target="_blank" rel="noopener">FX Risk</a>
              <a href="legal/ndpr.html" target="_blank" rel="noopener">NDPR Compliance</a>
            </div>
          </form>
        )}

        {mode === 'confirm' && (
          <form onSubmit={onConfirmSubmit} className="nsl-form" noValidate>
            <div style={{ marginBottom: 16, fontSize: 14, color: '#374151' }}>
              We sent a 6-digit verification code to{' '}
              <strong>{signupEmail || '(your email)'}</strong>. Enter it below to verify your account.
            </div>

            <label className="nsl-label" htmlFor="nsl-confirm-code">Verification Code</label>
            <input
              id="nsl-confirm-code"
              className="nsl-input"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              required
            />

            {err && <div className="nsl-err" role="alert">{err}</div>}

            <button type="submit" className="nsl-btn" disabled={busy}>
              {busy ? 'Verifying...' : 'Verify Email'}
            </button>

            <div className="nsl-help" style={{ marginTop: 12 }}>
              Didn't get a code?{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); onResendCode(); }}>Resend code</a>
              {' '}&middot;{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); setMode('login'); setLocalErr(undefined); setInfoMsg(undefined); }}>Back to login</a>
            </div>
          </form>
        )}

        {mode === 'forgot-request' && (
          <form onSubmit={onForgotSubmit} className="nsl-form" noValidate>
            <div style={{ marginBottom: 16, fontSize: 14, color: '#374151' }}>
              Enter the email associated with your account. We'll send you a 6-digit code to reset your password.
            </div>

            <label className="nsl-label" htmlFor="nsl-forgot-email">Email Address</label>
            <input
              id="nsl-forgot-email"
              type="email"
              className="nsl-input"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              required
            />

            {err && <div className="nsl-err" role="alert">{err}</div>}

            <button type="submit" className="nsl-btn" disabled={busy}>
              {busy ? 'Sending code...' : 'Send Reset Code'}
            </button>

            <div className="nsl-help" style={{ marginTop: 12 }}>
              <a href="#" onClick={(e) => { e.preventDefault(); setMode('login'); setLocalErr(undefined); setInfoMsg(undefined); }}>Back to login</a>
            </div>
          </form>
        )}

        {mode === 'forgot-confirm' && (
          <form onSubmit={onConfirmForgotSubmit} className="nsl-form" noValidate>
            <div style={{ marginBottom: 16, fontSize: 14, color: '#374151' }}>
              We sent a 6-digit code to{' '}
              <strong>{forgotEmail || '(your email)'}</strong>. Enter it below along with your new password.
            </div>

            <label className="nsl-label" htmlFor="nsl-forgot-code">Verification Code</label>
            <input
              id="nsl-forgot-code"
              className="nsl-input"
              value={forgotCode}
              onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              required
            />

            <label className="nsl-label" htmlFor="nsl-forgot-newpw">New Password</label>
            <input
              id="nsl-forgot-newpw"
              type="password"
              className="nsl-input"
              value={forgotNewPw}
              onChange={(e) => setForgotNewPw(e.target.value)}
              autoComplete="new-password"
              required
            />
            <div className="nsl-hint" style={{ fontSize: 11, color: '#6b7280', marginTop: -8, marginBottom: 8 }}>
              At least 8 characters with uppercase, lowercase, number, and symbol.
            </div>

            <label className="nsl-label" htmlFor="nsl-forgot-newpw2">Confirm New Password</label>
            <input
              id="nsl-forgot-newpw2"
              type="password"
              className="nsl-input"
              value={forgotNewPw2}
              onChange={(e) => setForgotNewPw2(e.target.value)}
              autoComplete="new-password"
              required
            />

            {err && <div className="nsl-err" role="alert">{err}</div>}

            <button type="submit" className="nsl-btn" disabled={busy}>
              {busy ? 'Resetting password...' : 'Reset Password'}
            </button>

            <div className="nsl-help" style={{ marginTop: 12 }}>
              <a href="#" onClick={(e) => { e.preventDefault(); setMode('forgot-request'); setLocalErr(undefined); setInfoMsg(undefined); }}>Didn't get a code? Try again</a>
              {' '}&middot;{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); setMode('login'); setLocalErr(undefined); setInfoMsg(undefined); }}>Back to login</a>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

